import React from "react";
import { Link } from "react-router-dom";
import {
  MdConstruction,
  MdEngineering,
  MdAnalytics,
  MdBusiness,
  MdArrowForward,
} from "react-icons/md";
import {
  FaHardHat,
  FaTruck,
  FaFileInvoice,
  FaChartLine,
  FaHandshake,
  FaBuilding,
  FaTools,
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const colors = {
  green: "#2e7d32", // Vert principal
  greenLight: "#4caf50", // Vert clair
  yellow: "#ffc107", // Jaune
  red: "#d32f2f", // Rouge
  darkGrey: "#333333",
  lightGrey: "#f5f5f5",
  white: "#ffffff",
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 25px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  border-top: 4px solid ${(props) => props.borderColor || colors.green};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 38px;
  color: ${(props) => props.color || colors.green};
  margin-bottom: 18px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: ${colors.darkGrey};
  position: relative;
  padding-bottom: 12px;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: ${(props) => props.underlineColor || colors.green};
  }
`;

const ServiceDescription = styled.p`
  color: #555;
  font-size: 16px;
  line-height: 1.6;
`;

const FeatureCard = styled.div`
  padding: 25px;
  background: ${colors.white};
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border-bottom: 3px solid ${(props) => props.borderColor || colors.yellow};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 42px;
  margin-bottom: 15px;
  color: ${(props) => props.color || colors.green};
`;

const Section = styled.section`
  padding: 60px 0;
  background: ${(props) => props.background || colors.white};
`;

const ListItem = styled.li`
  display: flex;
  align-items: start;
  margin-bottom: 12px;
`;

const BulletPoint = styled.div`
  color: ${(props) => props.color || colors.green};
  margin-right: 10px;
  font-weight: bold;
  font-size: 18px;
  line-height: 1.4;
`;

const HeroSection = styled.section`
  background: linear-gradient(
      to right,
      rgba(46, 125, 50, 0.85),
      rgba(76, 175, 80, 0.75)
    ),
    url("/assets/images/batiment-travaux-publics.jpg") no-repeat center center;
  background-size: cover;
  background-position: center;
  height: 350px;
  display: flex;
  align-items: center;
  color: ${colors.white};
`;

const HeroContent = styled.div`
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroIcon = styled.div`
  background: ${colors.white};
  width: 160px;
  height: 160px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: ${fadeIn} 1s ease-out forwards;
  animation-delay: 0.3s;
  opacity: 0;

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(to right, ${colors.green}, ${colors.greenLight});
  padding: 40px 0;
  color: ${colors.white};
  position: relative;
  width: 80%; /* Réduit la largeur (ajuste selon besoin) */
  max-width: 900px; /* Définit une largeur max pour éviter l'étirement */
  margin: 0 auto; /* Centre horizontalement */
  border-radius: 12px; /* (Optionnel) Ajoute un effet plus esthétique */
`;

const CTAContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const CTAButton = styled.button`
  background-color: ${colors.yellow};
  color: ${colors.darkGrey};
  font-weight: 600;
  font-size: 16px;
  padding: 12px 28px;
  border-radius: 4px;
  border: none;
  transition: all 0.3s ease;
  cursor: pointer;
  width: fit-content; /* Ajuste la largeur au contenu */
  display: block; /* Pour que margin auto fonctionne */
  margin: 0 auto; /* Centre horizontalement */

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    background: ${colors.white};
    color: ${colors.green};
  }
`;

const TestimonialCard = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  position: relative;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  border-left: 4px solid ${(props) => props.borderColor || colors.yellow};
  
  &:before {
    content: """;
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 60px;
    color: rgba(46, 125, 50, 0.1);
    font-family: Georgia, serif;
  }
  
  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const QuoteText = styled.p`
  font-style: italic;
  color: #555;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
  padding-left: 15px;
`;

const ClientInfo = styled.div`
  display: flex;
  align-items: center;
`;

const ClientAvatar = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(to right, ${colors.green}, ${colors.greenLight});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: ${colors.white};
  font-weight: bold;
  font-size: 18px;
`;

const IntroBox = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  margin-top: -40px;
  position: relative;
  z-index: 10;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
`;

const ActionButton = styled.button`
  background: ${(props) => (props.secondary ? colors.white : colors.green)};
  color: ${(props) => (props.secondary ? colors.green : colors.white)};
  font-weight: 600;
  font-size: 16px;
  padding: 12px 24px;
  border-radius: 4px;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
    background: ${(props) =>
    props.secondary ? colors.white : colors.greenLight};
  }

  svg {
    margin-left: 8px;
  }
`;

const SectionTitle = styled.h3`
  font-weight: 700;
  position: relative;
  padding-bottom: 12px;
  margin-bottom: 20px;
  color: ${colors.darkGrey};

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: ${(props) => (props.center ? "50%" : "0")};
    transform: ${(props) => (props.center ? "translateX(-50%)" : "none")};
    width: 60px;
    height: 3px;
    background: ${(props) => props.underlineColor || colors.green};
  }
`;

const FeatureBox = styled.div`
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const FeatureBoxIcon = styled.div`
  font-size: 24px;
  color: ${(props) => props.color || colors.green};
  margin-right: 15px;
  min-width: 40px;
`;

const BatimentTravauxPublics = () => {
  const services = [
    {
      icon: <FaFileInvoice />,
      title: "Comptabilité, Fiscalité et Paie",
      description:
        "Solutions comptables adaptées aux spécificités du secteur BTP pour une gestion optimale de votre entreprise.",
      color: colors.green,
      borderColor: colors.green,
      items: [
        "Tenue et révision comptable adaptée aux spécificités du BTP",
        "Établissement des comptes annuels et liasses fiscales",
        "Gestion de la TVA et différents taux applicables",
        "Optimisation fiscale pour réduire votre charge fiscale",
        "Gestion de la paie avec maîtrise des conventions BTP",
        "Assistance en cas de contrôle fiscal",
      ],
    },
    {
      icon: <FaChartLine />,
      title: "Gestion et Pilotage de Chantier",
      description:
        "Outils et conseils pour suivre et améliorer la performance de vos chantiers BTP.",
      color: colors.yellow,
      borderColor: colors.yellow,
      items: [
        "Comptabilité analytique et suivi des coûts par chantier",
        "Situations de travaux et suivi des avancements",
        "Tableaux de bord et KPI pour suivre vos performances",
        "Budgets prévisionnels et suivi budgétaire par chantier",
        "Optimisation des coûts et identification d'économies",
        "Gestion de trésorerie et prévisions de flux",
      ],
    },
    {
      icon: <MdBusiness />,
      title: "Conseil et Accompagnement Stratégique",
      description:
        "Un accompagnement sur mesure pour développer durablement votre entreprise du BTP.",
      color: colors.red,
      borderColor: colors.red,
      items: [
        "Accompagnement à la création et reprise d'entreprise",
        "Conseil en investissement pour acquisition de matériel",
        "Évaluation d'entreprise pour cession ou transmission",
        "Conseil en gestion des RH et fidélisation",
        "Transformation digitale et solutions logicielles",
        "Aide aux appels d'offres et dossiers de candidature",
      ],
    },
  ];

  const features = [
    {
      icon: <FaHardHat />,
      title: "Spécialistes du BTP",
      description:
        "Nous connaissons parfaitement les spécificités comptables, fiscales et sociales du secteur du bâtiment et des travaux publics.",
      color: colors.green,
      borderColor: colors.green,
    },
    {
      icon: <FaTools />,
      title: "Solutions Sur-mesure",
      description:
        "Nous adaptons nos outils et nos conseils à la taille et aux besoins spécifiques de votre entreprise.",
      color: colors.yellow,
      borderColor: colors.yellow,
    },
    {
      icon: <FaTruck />,
      title: "Suivi de Chantier",
      description:
        "Nous mettons en place des outils performants pour optimiser la gestion et la rentabilité de vos chantiers.",
      color: colors.red,
      borderColor: colors.red,
    },
  ];

  const testimonials = [
    {
      quote:
        "Depuis que nous travaillons avec CINEF-EC, la gestion de nos chantiers s'est considérablement améliorée. Nous avons une vision claire de notre rentabilité et pouvons prendre des décisions en toute connaissance de cause.",
      name: "Jean Dupont",
      position: "Directeur, Entreprise de construction",
      initial: "JD",
      borderColor: colors.green,
    },
    {
      quote:
        "Leur expertise en matière de réglementation BTP nous a permis d'optimiser notre fiscalité tout en restant parfaitement en règle. Un partenaire de confiance pour notre développement.",
      name: "Marie Martin",
      position: "Gérante, Société de travaux publics",
      initial: "MM",
      borderColor: colors.yellow,
    },
  ];

  return (
    <>
      <Header />

      {/* Hero Section */}
      <HeroSection>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <HeroContent>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  Bâtiment & Travaux Publics
                </h1>
                <p className="text-lg mb-8">
                  Maîtrisez vos chantiers, optimisez votre rentabilité,
                  sécurisez votre croissance
                </p>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li
                      className="breadcrumb-item active text-white"
                      aria-current="page"
                    >
                      Bâtiment & Travaux Publics
                    </li>
                  </ol>
                </nav>
              </HeroContent>
            </div>
          </div>
        </div>
      </HeroSection>

      {/* Introduction */}
      <Section>
        <div className="container mx-auto px-4">
          <IntroBox>
            <div className="row">
              <div className="col-12">
                <h2 className="text-center font-bold text-2xl mb-5">
                  Optimisez la performance et la rentabilité de vos chantiers
                  avec notre expertise comptable spécialisée BTP
                </h2>
                <p className="text-gray-600 text-center text-lg mb-0">
                  Le secteur du BTP est complexe et exigeant : gestion de
                  chantiers, suivi des coûts, personnel nombreux et mobile,
                  réglementations spécifiques, forte concurrence... CINEF-EC
                  comprend parfaitement vos enjeux. Nous sommes spécialisés dans
                  l'accompagnement des entreprises du BTP et nous vous offrons
                  une expertise complète, de la comptabilité quotidienne à la
                  stratégie de développement, pour vous permettre de vous
                  concentrer sur votre cœur de métier.
                </p>
              </div>
            </div>
          </IntroBox>
        </div>
      </Section>

      {/* Services Section */}
      <Section>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <SectionTitle center underlineColor={colors.green}>
              Nos services spécialisés
            </SectionTitle>
            <p className="text-gray-600 text-lg">
              Des solutions adaptées aux besoins spécifiques de votre entreprise
              du BTP
            </p>
          </div>

          <div className="row">
            {services.map((service, index) => (
              <div className="col-md-6 col-lg-4 mb-4" key={index}>
                <ServiceCard
                  borderColor={service.borderColor}
                  delay={`${index * 0.1}s`}
                >
                  <ServiceIcon color={service.color}>
                    {service.icon}
                  </ServiceIcon>
                  <ServiceTitle underlineColor={service.color}>
                    {service.title}
                  </ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  <ul className="ps-3 mt-4">
                    {service.items.map((item, idx) => (
                      <ListItem key={idx}>
                        <BulletPoint color={service.color}>•</BulletPoint>
                        <span>{item}</span>
                      </ListItem>
                    ))}
                  </ul>
                </ServiceCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <CTASection>
        <CTAContainer>
          <h3 className="font-bold text-2xl mb-4">
            Prêt à optimiser la gestion de vos chantiers ?
          </h3>
          <p className="text-lg mb-6">
            Contactez-nous dès aujourd'hui pour une consultation gratuite et
            personnalisée.
          </p>
          <Link to="/contact" className="btn bg-danger">Demander un rendez-vous</Link >
        </CTAContainer>
      </CTASection>

      {/* Features Section */}
      <Section>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <SectionTitle center underlineColor={colors.yellow}>
              Pourquoi nous choisir ?
            </SectionTitle>
          </div>

          <div className="row justify-content-center">
            {features.map((feature, index) => (
              <div className="col-md-4 col-sm-6 mb-4" key={index}>
                <FeatureCard borderColor={feature.borderColor}>
                  <FeatureIcon color={feature.color}>
                    {feature.icon}
                  </FeatureIcon>
                  <h4 className="font-bold text-lg mb-3">{feature.title}</h4>
                  <p className="text-gray-600 mb-0">{feature.description}</p>
                </FeatureCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Corporate Finance Section */}
      <Section>
        <div className="container mx-auto px-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <SectionTitle underlineColor={colors.red}>
                Solutions de Corporate Finance
              </SectionTitle>
              <p className="text-gray-600 mb-4">
                Notre équipe d'experts vous accompagne dans vos opérations
                financières stratégiques pour soutenir votre croissance et vos
                projets d'entreprise.
              </p>

              <FeatureBox>
                <FeatureBoxIcon color={colors.green}>
                  <FaHandshake />
                </FeatureBoxIcon>
                <div>
                  <h5 className="font-bold mb-2">Financement de projets</h5>
                  <p className="text-gray-600">
                    Accompagnement pour trouver des financements adaptés à vos
                    projets de construction et développement.
                  </p>
                </div>
              </FeatureBox>

              <FeatureBox>
                <FeatureBoxIcon color={colors.yellow}>
                  <FaHandshake />
                </FeatureBoxIcon>
                <div>
                  <h5 className="font-bold mb-2">Fusions et Acquisitions</h5>
                  <p className="text-gray-600">
                    Conseil et assistance lors de cessions, acquisitions, ou
                    rapprochements d'entreprises du BTP.
                  </p>
                </div>
              </FeatureBox>

              <FeatureBox>
                <FeatureBoxIcon color={colors.red}>
                  <FaHandshake />
                </FeatureBoxIcon>
                <div>
                  <h5 className="font-bold mb-2">Gestion de croissance</h5>
                  <p className="text-gray-600">
                    Accompagnement dans vos projets d'expansion et
                    d'accroissement de votre part de marché.
                  </p>
                </div>
              </FeatureBox>
            </div>

            <div className="col-lg-6">
              <div className="bg-light p-4 p-lg-5 rounded shadow-sm">
                <div className="text-center mb-4">
                  <FaBuilding
                    style={{ fontSize: "64px", color: colors.green }}
                  />
                </div>
                <h4 className="font-bold text-center mb-3">
                  Une expertise spécifique pour le BTP
                </h4>
                <p className="text-gray-600 text-center">
                  Nous apportons des solutions adaptées à votre secteur
                  d'activité, en tenant compte des spécificités des chantiers,
                  des marchés publics, et des cycles de facturation propres au
                  BTP.
                </p>
                <div className="text-center mt-4">
                  <Link to="/businesscreationform" className="btn btn-success px-4 py-2 fw-bold">
                    Contactez nos experts
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section background={colors.lightGrey}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <SectionTitle center underlineColor={colors.green}>
              Témoignages de nos clients
            </SectionTitle>
          </div>

          <div className="row">
            {testimonials.map((testimonial, index) => (
              <div className="col-lg-6 mb-4" key={index}>
                <TestimonialCard
                  borderColor={testimonial.borderColor}
                  delay={`${index * 0.2}s`}
                >
                  <QuoteText>{testimonial.quote}</QuoteText>
                  <ClientInfo>
                    <ClientAvatar>{testimonial.initial}</ClientAvatar>
                    <div>
                      <h5 className="font-bold mb-0">{testimonial.name}</h5>
                      <p className="text-gray-500 text-sm mb-0">
                        {testimonial.position}
                      </p>
                    </div>
                  </ClientInfo>
                </TestimonialCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Footer />

      <style>{`
        .breadcrumb {
          background: transparent;
          font-size: 16px;
          margin-top: 15px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          h1, h2 {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </>
  );
};

export default BatimentTravauxPublics;
